
import Vue from 'vue';
import BaseIcon from '@core/components/UI/BaseIcon.vue';
import { HeadlineWithRelatedArticles } from '@headlines/types/headline';
import { gridConfig } from '@core/config/grid.config';

interface Props {
  headline: HeadlineWithRelatedArticles;
  onTop: boolean;
  size: string;
}

interface Data {
  iconList: Record<string, string>;
  contentType: {
    career: boolean;
    detector: boolean;
    detectorCategory: boolean;
    video: boolean;
    gallery: boolean;
    stream: boolean;
    streamSoon: boolean;
    audio: boolean;
    trending: boolean;
    exclusive: boolean;
    research: boolean;
    bloomberg: boolean;
    economist: boolean;
    politicalAd: boolean;
  };
}

interface Computed {
  classModifier: Record<string, any>;
  isMobileView: boolean;
}

interface Methods {
  hasIcon: (icon: string) => boolean;
  hasTagId: (tagId: number) => boolean;
  isPrimaryCategory: (categoryId: number) => boolean;
  isNoPadding: (name: string) => boolean;
}

const iconList = {
  gallery: 'gallery',
  video: 'play',
  audio: 'microphone',
  trending: 'trending',
};

const careerIcons = [
  'frontPageMostPopularJobOffers',
  'frontPageLookingForTalents',
  'frontPageInterviewWithCLevelManagers',
  'frontPageRelevantInTheOffice',
  'frontPageItsInteresting',
  'frontPageHHRPerspective',
];

const careerTags = [89580921, 89580923, 89580941, 89580979, 89580981, 89969303];
const detectorCategories = [
  85827865, 79451385, 79451419, 85827867, 88121525, 88122145, 92718855, 92719795, 93953369, 93953457, 93481599, 92782775, 85827871, 79451335,
];
const politicalAdCategories = [84666519, 85038351, 80167949, 91841171];

interface Process {
  client: boolean;
}

declare let process: Process;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseIcon,
  },
  props: {
    headline: {
      type: Object,
      required: true,
    },
    onTop: {
      type: Boolean,
      default: true,
      required: false,
    },
    size: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      iconList,
      contentType: {
        career:
          !!this.headline.tags?.items?.find((tag) => careerTags.includes(tag.id)) ||
          !!this.headline.settings?.find((setting) => careerIcons.includes(setting.key) && setting.value === true),
        detector: this.headline.primaryCategory.parentCategory.id === 85827763 || this.headline.primaryCategory.parentCategory.id === 79440681,
        detectorCategory: detectorCategories.includes(this.headline.primaryCategory?.id),
        video:
          (this.headline.relatedEntities?.hasVideo && this.headline.primaryCategory?.channel.url !== 'www.delfi.lt/video') ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageVideo' && setting.value === true),
        gallery:
          this.headline.relatedEntities?.hasGallery ||
          (this.headline.settings?.find((setting) => setting.key === 'icon.image')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageImage' && setting.value === true),
        stream:
          this.headline.relatedEntities?.hasStream ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageLiveVideo' && setting.value === true),
        streamSoon: this.headline.settings?.find((setting) => setting.key === 'streamSoon')?.value ?? false,
        sustainableCourse:
          !!this.headline.tags?.items?.find((tag) => [91160101].includes(tag.id)) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageSustainabilityCourse' && setting.value === true),
        audio:
          this.headline.relatedEntities?.hasAudio || !!this.headline.settings?.find((setting) => setting.key === 'frontPageAudio' && setting.value === true),
        trending: this.headline.settings?.find((setting) => setting.key === 'trending')?.value ?? false,
        exclusive: this.headline.settings?.find((setting) => setting.key === 'icon.exclusive')?.value ?? false,
        research:
          (this.headline.settings?.find((setting) => setting.key === 'icon.research')?.value ?? false) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageResearch' && setting.value === true) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPageInvestigativeJournalism' && setting.value === true),
        bloomberg:
          this.headline.primaryCategory.id === 79298779 ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 82146369) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'Bloomberg'),
        economist:
          !!this.headline.tags?.items?.find((tag) => tag.id === 49711978) ||
          (this.headline.settings?.find((setting) => setting.key === 'sources')?.value.length &&
            this.headline.settings?.find((setting) => setting.key === 'sources')?.value[0]['name'] === 'The Economist'),
        politicalAd:
          politicalAdCategories.includes(this.headline.primaryCategory?.id) ||
          !!this.headline.tags?.items?.find((tag) => tag.id === 71865184) ||
          !!this.headline.settings?.find((setting) => setting.key === 'frontPagePoliticalAd' && setting.value === true),
        premiere:
          !!this.headline.settings?.find((setting) => setting.key === 'frontPagePremiere' && setting.value === true) ||
          (this.headline.content.paywall?.enabled && this.headline.content.paywall.disableAt && new Date() < new Date(this.headline.content.paywall.disableAt)),
        interview: !!this.headline.settings?.find((setting) => setting.key === 'frontPageInterview' && setting.value === true),
        invest: !!this.headline.settings?.find((setting) => setting.key === 'frontPageInvest' && setting.value === true),
        renew: !!this.headline.settings?.find((setting) => setting.key === 'frontPageRenewed' && setting.value === true),
        graph: !!this.headline.settings?.find((setting) => setting.key === 'frontPageGraph' && setting.value === true),
      },
    };
  },
  computed: {
    classModifier() {
      return {
        'C-headline-icons--on-top': this.onTop,
        'C-headline-icons--small': this.contentType.streamSoon,
        [`C-headline-icons--${this.size}`]: !!this.size,
      };
    },
    isMobileView() {
      if (process.client) {
        const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;

        return window.screen.width <= gridConfig.breakpoints.lg || isIpad;
      } else {
        return this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
      }
    },
  },
  methods: {
    hasIcon(icon) {
      return !!this.headline.settings?.find((setting) => setting.key === icon && setting.value === true);
    },
    hasTagId(tagId) {
      return !!this.headline.tags?.items?.find((tag) => tag.id === tagId);
    },
    isPrimaryCategory(categoryId) {
      return this.headline.primaryCategory.id === categoryId;
    },
    isNoPadding(name) {
      const noPaddingIcons: string[] = ['career', 'exclusive', 'political', 'sustainableCourse', 'research', 'invest'];

      return this.isMobileView && noPaddingIcons.includes(name);
    },
  },
});
